import { FormSuccess } from 'Components/Form/Success';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from '../../../Components/Container';
import { useEffect } from 'react';
import { addUsersGroup, editUsersGroup, getUsersGroupById, updateUsersGroupUsers } from 'services/users-groups-service';
import { FormUsersGroup } from './formUsersGroup';
/** Third Library Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useQuery } from 'react-query';
import { PageHelper } from '../../../Components/PageHelper';
import { TransferList } from '../../../Components/TransferList';
import { getUsers } from 'services/users-service';
import { Box, Typography, Divider, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao formulário de Grupos de Usuários! Aqui você pode criar ou editar grupos para organizar seus usuários.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique aqui no título para voltar à lista de Grupos de Usuários.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="group-name"]',
    content: 'Digite o nome do grupo de usuários. Este nome será usado para identificar o grupo ao enviar políticas.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="group-active"]',
    content: 'Marque esta caixa para ativar o grupo. Grupos inativos não aparecerão nas opções de envio de políticas.',
    disableScrolling: true,
    placement: 'bottom',
  },
  {
    target: '[data-tour="form-actions"]',
    content: 'Use "Cancelar" para descartar as alterações ou "Salvar" para criar/atualizar o grupo.',
    placement: 'top',
  },
  {
    target: 'body',
    content: 'Abaixo do formulário, você encontrará uma seção para selecionar usuários. Use as caixas duplas para adicionar ou remover usuários do grupo.',
    placement: 'center',
  },
  {
    target: 'body',
    content: 'Tour concluído! Preencha as informações, selecione os usuários e salve o grupo.',
    placement: 'center',
  },
];

export const UsersGroupsForm = () => {
  const { t } = useTranslation(['user', 'common']);
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const [idUsersGroup, setIdUsersGroup] = useState(null);
  const [finished, setFinished] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [originalUserIds, setOriginalUserIds] = useState([]); // IDs dos usuários originais do grupo
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      active: false,
    },
  });

  // Busca todos os usuários disponíveis
  const { data: allUsers, isLoading: isLoadingUsers } = useQuery(
    ['getAllUsers', selectedTenant.slug],
    async () => {
      const result = await getUsers(selectedTenant.slug, 1, 1000, '', '', true);
      return result?.data?.data || [];
    },
    {
      enabled: !!selectedTenant.slug,
    }
  );

  // Busca o grupo de usuários (se estiver editando)
  const { data: userGroup } = useQuery(['getUsersGroups', id], async () => {
    if (!id) return null;
    const resUsersGroup = await getUsersGroupById(id);

    if (!resUsersGroup.data) return null;

    return resUsersGroup.data;
  });

  // Atualiza listas de usuários disponíveis e selecionados quando os dados carregam
  useEffect(() => {
    if (!allUsers) return;

    if (id && userGroup) {
      // Modo edição: filtra usuários que têm esse grupo no campo 'groups'
      const groupName = userGroup.name;
      
      const usersInGroup = allUsers.filter(user => 
        user.groups?.some(g => g.companyKey === selectedTenant.slug && g.name === groupName)
      );
      
      const usersNotInGroup = allUsers.filter(user => 
        !user.groups?.some(g => g.companyKey === selectedTenant.slug && g.name === groupName)
      );
      
      setSelectedUsers(usersInGroup); // Usuários que têm o grupo
      setAvailableUsers(usersNotInGroup); // Usuários que não têm o grupo
      setOriginalUserIds(usersInGroup.map(u => u._id)); // Salva os IDs originais
    } else if (!id) {
      // Modo criação: todos os usuários estão disponíveis
      setAvailableUsers(allUsers);
      setSelectedUsers([]);
      setOriginalUserIds([]);
    }
  }, [allUsers, id, userGroup, selectedTenant.slug]);

  const formatSubmit = values => {
    return {
      name: values.name,
      active: values.active,
      companyKey: selectedTenant.slug,
    };
  };

  const handleComplete = async () => {
    try {
      const formattedForm = formatSubmit(getValues());
      const res = id ? await editUsersGroup(id, formattedForm) : await addUsersGroup(formattedForm);
      if (res.status === 200 || res.status === 201) {
        const savedGroupId = res.data._id ?? id;
        setIdUsersGroup(savedGroupId);

        const userIds = selectedUsers.map(user => user._id);
        await updateUsersGroupUsers(savedGroupId, userIds);

        SwalReact.fire({
          title: t('user:alert.successTitle'),
          text: t('user:groups.success.saved'),
          icon: 'success',
        });
        if (!id) {
          setFinished(true);
        }
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar/editar o Grupo de Usuário!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleSaveUsers = async () => {
    try {
      if (!id && !idUsersGroup) {
        SwalReact.fire({
          title: t('user:alert.warningTitle'),
          text: t('user:groups.warning.saveFirst'),
          icon: 'warning',
        });
        return;
      }

      const groupId = id || idUsersGroup;
      const userIds = selectedUsers.map(user => user._id);
      const res = await updateUsersGroupUsers(groupId, userIds);
      
      if (res.status === 200 || res.status === 201) {
        SwalReact.fire({
          title: t('user:alert.successTitle'),
          text: t('user:groups.success.usersUpdated'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar atualizar os usuários do grupo!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
    setIdUsersGroup(null);
    navigate('/users-groups/add');
  };

  useEffect(() => {
    if (!id) return;
    if (!userGroup) return;

    Object.keys(userGroup).forEach(key => {
      if (key !== 'users') { // Não seta users no formulário, já é tratado acima
        setValue(key, userGroup[key]);
      }
    });

    return () => {};
  }, [id, userGroup, setValue]);

  const handleUsersChange = (newSelected, newAvailable) => {
    setSelectedUsers(newSelected);
    setAvailableUsers(newAvailable);
  };

  return (
    <>
      {!finished && (
        <PageHelper 
          steps={tourSteps}
          tourKey="users-groups-form-tour"
          autoStart={true}
          showButton={true}
        />
      )}
      
      <Container
        title={id ? t('user:groups.form.editTitle') : t('user:groups.form.addTitle')}
        breadcrumb={t('user:groups.breadcrumb')}
        linkPage="/users-groups"
      >
      <div className="card-content">
        {!finished && (
          <>
            <FormUsersGroup
              control={control}
              register={register}
              handleComplete={handleComplete}
              handleSubmit={handleSubmit}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
            />
            
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('user:groups.form.selectUsers')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {t('user:groups.form.selectUsersDescription')}
              </Typography>
              
              {isLoadingUsers ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>{t('user:groups.form.loadingUsers')}</Typography>
                </Box>
              ) : (
                <>
                  <TransferList
                    available={availableUsers}
                    selected={selectedUsers}
                    onChange={handleUsersChange}
                    leftTitle={t('user:groups.form.availableUsers')}
                    rightTitle={t('user:groups.form.groupUsers')}
                    getItemLabel={(user) => `${user.name} (${user.email})`}
                    getItemValue={(user) => user._id}
                    originalItems={originalUserIds}
                    searchable={true}
                    height="350px"
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveUsers}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('user:groups.form.saveUsers')}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}
        {finished && (
          <FormSuccess
            title={t('user:groups.form.successTitle')}
            labelStartAgain={t('user:groups.form.addAnother')}
            handleStartAgain={() => handleReset()}
            labelShow={t('user:groups.form.viewDetails')}
            handleShow={() => navigate('/users-groups/show/' + idUsersGroup)}
          />
        )}
      </div>
    </Container>
    </>
  );
};
